import { addDays } from "date-fns";

import type { SchedulerContext } from "@/types/scheduler-machine";

import type { SlotsSearchInput } from "./fetchSlots.util";

const SIXTY_DAYS_OF_SLOTS = 60;

function toDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getSlotsSearchEndDate(searchDate: Date): Date {
  const maxSchedulingDate = addDays(new Date(), SIXTY_DAYS_OF_SLOTS);
  const expandedEndDate = addDays(searchDate, SIXTY_DAYS_OF_SLOTS);

  return expandedEndDate < maxSchedulingDate ? expandedEndDate : maxSchedulingDate;
}

export function getSlotsSearchStartDate(context: SchedulerContext): Date {
  if (!context.slotsSearchDate) return new Date();

  const searchDate = new Date(`${context.slotsSearchDate}T00:00:00`);

  // Date normalizes impossible calendar dates (for example, February 31) into the next month.
  // Require a valid parse and an exact round trip before using the date for the search.
  return Number.isNaN(searchDate.getTime()) || toDateString(searchDate) !== context.slotsSearchDate
    ? new Date()
    : searchDate;
}

export function buildSlotsSearchInput({
  context,
}: {
  context: SchedulerContext;
}): SlotsSearchInput {
  const searchDate = getSlotsSearchStartDate(context);
  const endDate = getSlotsSearchEndDate(searchDate);

  return {
    facilityCode: context.facility?.code ?? undefined,
    digitalVisitReason: context.reasonForVisit ?? undefined,
    firstName: context.patientData.firstName,
    lastName: context.patientData.lastName,
    dateOfBirth: context.patientData.dateOfBirth,
    email: context.patientData.emailAddress ?? undefined,
    phone: context.patientData.phoneNumber?.replaceAll(/\D/g, "") ?? undefined,
    // Omitted when no questionnaire is loaded — eAPI treats a questionnaireId it did not issue
    // as a real reference, so a placeholder is not equivalent to leaving the key off.
    questionnaireId: context.questionnaire?.questionnaireBy?.questionnaireId ?? undefined,
    questionnaireAnsweredQuestions: context.needBasedQuestionsAnswers,
    startDate: toDateString(searchDate),
    endDate: toDateString(endDate),
    restUrl: context.restUrl,
  };
}
